import type { BackofficeOutboundCall } from '@nava/backoffice-bff-contracts';
import { DetailRow } from './DetailRow';
import { formatDateTime } from '../format';

const states: Record<BackofficeOutboundCall['status'], string> = {
  queued: 'Initialized',
  dialing: 'Dialing',
  answered: 'Answered',
  connecting_agent: 'Connecting agent',
  in_conversation: 'In conversation',
  ended: 'Ended',
};
const outcomes: Record<NonNullable<BackofficeOutboundCall['outcome']>, string> = {
  unknown: 'Awaiting confirmation',
  completed: 'Completed',
  no_answer: 'No answer',
  busy: 'Busy',
  rejected: 'Rejected',
  failed: 'Failed',
  agent_connection_failed: 'Agent connection failed',
  cancelled: 'Cancelled',
  max_duration: 'Maximum duration reached',
};
const results: Record<BackofficeOutboundCall['resultsStatus'], string> = {
  not_applicable: 'Not applicable',
  pending: 'Pending',
  ready: 'Ready',
  failed: 'Failed',
};
function callLabel(call: BackofficeOutboundCall) {
  const label =
    call.status === 'ended' && call.outcome && call.outcome !== 'unknown'
      ? `${states.ended}: ${outcomes[call.outcome]}`
      : states[call.status];
  return label;
}
export function OutboundCallBadge({ call }: { call: BackofficeOutboundCall }) {
  const label = callLabel(call);
  return (
    <span
      className="inline-flex rounded-full bg-neutral-100 px-2.5 py-1 text-small font-medium text-neutral-700"
      aria-label={`Outbound call: ${label}`}
    >
      {label}
    </span>
  );
}
export function OutboundCallDetails({ call }: { call: BackofficeOutboundCall }) {
  return (
    <div className="space-y-2">
      <DetailRow label="Outbound call" value={callLabel(call)} />
      <DetailRow
        label="Dispatch"
        value={
          call.dispatchState === 'unknown'
            ? 'Awaiting provider confirmation'
            : call.dispatchState === 'confirmed'
              ? 'Confirmed'
              : 'Not dispatched'
        }
      />
      <DetailRow label="Call outcome" value={call.outcome ? outcomes[call.outcome] : 'Pending'} />
      <DetailRow label="Call results" value={results[call.resultsStatus]} />
      <DetailRow
        label="Phone answered"
        value={call.answeredAt ? formatDateTime(call.answeredAt) : '—'}
      />
      <DetailRow label="Call ended" value={call.endedAt ? formatDateTime(call.endedAt) : '—'} />
      <DetailRow label="Last observed" value={formatDateTime(call.observedAt)} />
    </div>
  );
}
